import { Task, TaskStatus, TaskWithProgress } from '../../../types/video';
import createTaskError from '../../../utils/video/createTaskError';

import { IProcessingTask, ITask } from './types';

class VideoEditorApi {
  readonly baseUrl: string;
  readonly headers: Record<string, string>;

  constructor(picsartApiUrl: string, picsartApiKey: string) {
    this.baseUrl = `${picsartApiUrl}/web-video-editor-v2`;
    this.headers = {
      'x-api-key': picsartApiKey,
      'Content-Type': 'application/json',
    };
  }

  createPreprocessingTask = async (url: string, signal?: AbortSignal): Promise<Task<ITask>> => {
    try {
      const res = await fetch(`${this.baseUrl}/v3/preprocessing`, {
        method: 'POST',
        headers: this.headers,
        credentials: 'same-origin',
        mode: 'cors',
        signal,
        body: JSON.stringify({
          content: {
            url,
          },
        }),
      });

      const data = await res.json();
      return { status: TaskStatus.Done, taskId: data.taskId };
    } catch (error) {
      return createTaskError(error);
    }
  };

  createTask = async (
    processingTask: IProcessingTask,
    signal?: AbortSignal,
  ): Promise<Task<ITask>> => {
    try {
      const res = await fetch(`${this.baseUrl}/v3/tasks`, {
        method: 'POST',
        headers: this.headers,
        credentials: 'same-origin',
        mode: 'cors',
        signal,
        body: JSON.stringify(processingTask),
      });

      const data = await res.json();
      return { status: TaskStatus.Done, taskId: data.taskId };
    } catch (error) {
      return createTaskError(error);
    }
  };

  getTaskById = async (
    taskId: string,
    signal?: AbortSignal,
  ): Promise<TaskWithProgress<{ url: string }, number>> => {
    try {
      const res = await fetch(`${this.baseUrl}/v3/tasks/${taskId}`, {
        method: 'GET',
        headers: this.headers,
        credentials: 'same-origin',
        mode: 'cors',
        signal,
      });

      const data = await res.json();

      switch (data.status) {
        case 'running':
        case 'queued':
          return { status: TaskStatus.InProgress, progress: data.progress };
        case 'done':
          return { status: TaskStatus.Done, url: data.result.url };
        default:
          return {
            status: TaskStatus.Error,
            error: data.error || 'Something went wrong',
          };
      }
    } catch (error) {
      return createTaskError(error);
    }
  };
}

export default VideoEditorApi;
